/**
 * PlanChangeFlow — orchestrates a plan change to completion.
 *
 *   buy / change-tier  → <PlanSelector> (tier cards + interval toggle)
 *        → free→paid : checkout() → Stripe redirect
 *        → existing  : previewUpgrade() → confirm → upgrade()
 *   interval (quick)   → straight to previewUpgrade() → confirm
 *
 * `variant="modal"` (default) renders the selector + confirm as overlays
 * (Dashboard / QR page). `variant="inline"` renders them as in-page panels —
 * used on /account/billing, where a modal over the billing page is redundant.
 *
 * HD-119: an annual→monthly (same/lower tier) change previews as mutation 'D'
 * (€0 now, future effective date); confirm shows that, scheduled-aware.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { UpgradeConfirmModal } from "@d-m-organization/pricing-ui";
import type {
  BillingInterval,
  CatalogProduct,
  CheckoutResult,
  UpgradeOpts,
  UpgradePreviewResult,
  UpgradeResult,
} from "@d-m-organization/pricing-ui";
import type { BillingRow } from "../lib/deriveBillingView.js";
import { PlanSelector } from "./PlanSelector.js";

export type PlanChangeIntent =
  | { readonly kind: "interval"; readonly target: BillingInterval }
  | { readonly kind: "tier" }
  | { readonly kind: "buy" };

export type PlanChangeFlowProps = {
  readonly row: BillingRow;
  readonly product: CatalogProduct;
  readonly intent: PlanChangeIntent;
  readonly initialTier?: "pro" | "business";
  readonly variant?: "modal" | "inline";
  readonly previewUpgrade: (o: UpgradeOpts) => Promise<UpgradePreviewResult>;
  readonly upgrade: (o: UpgradeOpts) => Promise<UpgradeResult>;
  readonly checkout: (o: { kind: "product"; product: string; tier: "pro" | "business"; interval: BillingInterval }) => Promise<CheckoutResult>;
  readonly onClose: () => void;
  readonly onApplied: () => void;
};

function tierLabel(t: string): string { return t.charAt(0).toUpperCase() + t.slice(1); }


export function PlanChangeFlow(props: PlanChangeFlowProps) {
  const { t } = useTranslation();
  const { row, product, intent } = props;
  const inline = props.variant === "inline";
  const isFreeNow = row.tier === "free";

  const [step, setStep] = useState<"select" | "confirm">(
    intent.kind === "interval" ? "confirm" : "select",
  );
  const [target, setTarget] = useState<{ tier: "pro" | "business"; interval: BillingInterval } | null>(
    intent.kind === "interval" ? { tier: row.tier as "pro" | "business", interval: intent.target } : null,
  );

  const [preview, setPreview] = useState<UpgradePreviewResult | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const opts: UpgradeOpts | null = useMemo(
    () => (target ? { kind: "product_tier_change", product: row.productId, tier: target.tier, interval: target.interval } : null),
    [target, row.productId],
  );

  useEffect(() => {
    if (step !== "confirm" || !opts) return;
    let alive = true;
    setIsPreviewLoading(true);
    setError(null);
    props.previewUpgrade(opts)
      .then((p) => { if (alive) { setPreview(p); setIsPreviewLoading(false); } })
      .catch((e: unknown) => { if (alive) { setError((e as Error)?.message ?? t("planChange.couldntLoadPreview")); setIsPreviewLoading(false); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, target?.tier, target?.interval]);

  const onChoose = useCallback(async (tier: "pro" | "business", interval: BillingInterval) => {
    if (isFreeNow) {
      setBusy(true);
      setError(null);
      try {
        const res = await props.checkout({ kind: "product", product: row.productId, tier, interval });
        if (typeof window !== "undefined") window.location.href = res.url;
      } catch (e) {
        setError((e as Error)?.message ?? t("planChange.couldntStartCheckout"));
        setBusy(false);
      }
      return;
    }
    setTarget({ tier, interval });
    setStep("confirm");
  }, [isFreeNow, props, row.productId]);

  const onConfirm = useCallback(async () => {
    if (!opts) return;
    setIsConfirming(true);
    setError(null);
    try {
      const locked: UpgradeOpts = preview ? { ...opts, prorationDate: preview.prorationDate } : opts;
      await props.upgrade(locked);
      props.onApplied();
    } catch (e) {
      setError((e as Error)?.message ?? t("planChange.somethingWentWrong"));
      setIsConfirming(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts, preview]);

  if (step === "select") {
    return (
      <PlanSelector
        product={product}
        currentTier={row.tier}
        currentInterval={row.interval}
        initialInterval={row.interval ?? "month"}
        initialTier={props.initialTier}
        busy={busy}
        error={error}
        variant={props.variant}
        onChoose={onChoose}
        onClose={props.onClose}
      />
    );
  }

  const targetLabel = target
    ? t("planChange.targetLabel", { tier: tierLabel(target.tier), interval: target.interval === "year" ? t("planChange.yearly") : t("planChange.monthly") })
    : t("planChange.confirmChange");

  if (inline) {
    return (
      <InlineConfirmPanel
        targetLabel={targetLabel}
        preview={preview}
        isPreviewLoading={isPreviewLoading}
        isConfirming={isConfirming}
        error={error}
        onConfirm={onConfirm}
        onCancel={props.onClose}
      />
    );
  }

  return (
    <UpgradeConfirmModal
      isOpen
      preview={preview}
      isPreviewLoading={isPreviewLoading}
      isConfirming={isConfirming}
      error={error}
      targetLabel={targetLabel}
      onConfirm={onConfirm}
      onCancel={props.onClose}
    />
  );
}

// ---------------------------------------------------------------------------
// Inline confirm panel (in-page counterpart to UpgradeConfirmModal)
// ---------------------------------------------------------------------------

const CC = { navy: "#261F59", purple: "#351DBD", muted: "#E3EBF6", white: "#fff", textMuted: "#6b7084", errorText: "#9b1c1c", amber: "#7a5b12", amberBg: "#FFF7E6" };

function money(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: (currency || "eur").toUpperCase() }).format(cents / 100);
  } catch {
    return "€" + (cents / 100).toFixed(2);
  }
}
function fmtDate(ms: number): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(ms));
}

function InlineConfirmPanel(props: {
  targetLabel: string;
  preview: UpgradePreviewResult | null;
  isPreviewLoading: boolean;
  isConfirming: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const { preview, isPreviewLoading, isConfirming, error } = props;
  const scheduled = preview?.mutation === "D";
  return (
    <section role="region" aria-label={props.targetLabel} style={{ background: CC.white, border: "1px solid " + CC.muted, borderRadius: 16, padding: "20px 22px 22px", marginTop: 20 }}>
      <h3 style={{ fontSize: 17, fontWeight: 800, color: CC.navy, margin: "0 0 12px" }}>{props.targetLabel}</h3>

      {isPreviewLoading || !preview ? (
        <p style={{ fontSize: 14, color: CC.textMuted, margin: "0 0 14px" }}>{t("planChange.calculatingAmount")}</p>
      ) : scheduled ? (
        <p style={{ fontSize: 14, color: CC.navy, margin: "0 0 14px" }}>
          <strong>{t("planChange.noChargeNow")}</strong> {t("planChange.planSwitchesOn", { date: fmtDate(preview.nextBillingDate) })}
        </p>
      ) : (
        <div style={{ fontSize: 14, color: CC.navy, margin: "0 0 14px", display: "flex", flexDirection: "column", gap: 4 }}>
          <div><strong>{t("planChange.dueToday", { amount: money(preview.amountDueCents, preview.currency) })}</strong></div>
          {preview.nextBillingAmountCents > 0 && (
            <div style={{ color: CC.textMuted, fontSize: 13 }}>
              {t("planChange.thenAmountOn", { amount: money(preview.nextBillingAmountCents, preview.currency), date: fmtDate(preview.nextBillingDate) })}
            </div>
          )}
          {preview.willEndTrial && (
            <div style={{ marginTop: 6, fontSize: 12, color: CC.amber, background: CC.amberBg, padding: "6px 10px", borderRadius: 8 }}>
              {t("planChange.confirmingEndsTrial")}
            </div>
          )}
        </div>
      )}

      {error && <p role="alert" style={{ color: CC.errorText, fontSize: 13, margin: "0 0 12px" }}>{error}</p>}

      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={props.onConfirm} disabled={isConfirming || isPreviewLoading}
          style={{ font: "inherit", fontSize: 14, fontWeight: 700, minHeight: 44, padding: "9px 18px", borderRadius: 9, border: "1px solid transparent", background: CC.purple, color: CC.white, cursor: isConfirming || isPreviewLoading ? "default" : "pointer", opacity: isConfirming || isPreviewLoading ? 0.6 : 1 }}>
          {isConfirming ? t("planChange.confirming") : scheduled ? t("planChange.confirmChange") : t("planChange.confirmAndPay")}
        </button>
        <button type="button" onClick={props.onCancel} disabled={isConfirming}
          style={{ font: "inherit", fontSize: 14, fontWeight: 600, minHeight: 44, padding: "9px 16px", borderRadius: 9, border: "1px solid " + CC.muted, background: CC.white, color: CC.navy, cursor: "pointer" }}>
          {t("planChange.cancel")}
        </button>
      </div>
    </section>
  );
}
